import type { WalletCoreSolanaSigner } from '@/lib/wallet-core/types'
import { type Connection, PublicKey } from '@solana/web3.js'
import bs58 from 'bs58'

// Minimum SOL required for gas + rent (in lamports)
const MIN_SOL_FOR_GAS = 0.005 * 1e9 // 0.005 SOL - enough for gas + token account rent

const CHALLENGE_PREFIX = 'Predicity faucet request'

// Alchemy's Solana signer initialization can lag a beat after auth — the
// `signer` reference is populated before its underlying client is fully
// usable, so the first signMessage call sometimes throws. Retry aggressively
// before giving up.
const SIGN_MAX_ATTEMPTS = 10
const SIGN_RETRY_DELAY_MS = 500

async function signChallengeWithRetry(
	signer: WalletCoreSolanaSigner,
	challenge: string
): Promise<Uint8Array> {
	const challengeBytes = new TextEncoder().encode(challenge)
	let lastError: unknown
	for (let attempt = 0; attempt < SIGN_MAX_ATTEMPTS; attempt++) {
		try {
			return await signer.signMessage(challengeBytes)
		} catch (err) {
			lastError = err
			if (attempt < SIGN_MAX_ATTEMPTS - 1) {
				await new Promise(resolve => setTimeout(resolve, SIGN_RETRY_DELAY_MS))
			}
		}
	}
	throw lastError instanceof Error
		? new Error(`Faucet signer not ready after retries: ${lastError.message}`)
		: new Error('Faucet signer not ready after retries')
}

/**
 * TEMPORARY: Auto-request gas from faucet if user doesn't have enough
 * TODO: Remove this once Alchemy fixes their gasless sponsorship API
 * and re-enable sponsored transactions
 */
export async function ensureGasBalance(
	connection: Connection,
	userAddress: string,
	signer: WalletCoreSolanaSigner
): Promise<void> {
	const pubkey = new PublicKey(userAddress)
	const balance = await connection.getBalance(pubkey, 'confirmed')

	if (balance >= MIN_SOL_FOR_GAS) {
		return
	}

	// Prove control of the address with a signed, time-bound challenge so
	// the faucet route can verify the requester owns the wallet. Burner
	// addresses without the key can't drain the faucet.
	const challenge = `${CHALLENGE_PREFIX}\nAddress: ${userAddress}\nTimestamp: ${Date.now()}`
	const signatureBytes = await signChallengeWithRetry(signer, challenge)
	const signatureB58 = bs58.encode(signatureBytes)

	const response = await fetch('/api/faucet', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			address: userAddress,
			message: challenge,
			signature: signatureB58,
		}),
	})

	const result = await response.json()

	if (!response.ok) {
		throw new Error(`Failed to get gas: ${result.error || 'Unknown error'}`)
	}

	if (result.signature) {
		for (let i = 0; i < 15; i++) {
			await new Promise(resolve => setTimeout(resolve, 500))
			const newBalance = await connection.getBalance(pubkey, 'confirmed')
			if (newBalance >= MIN_SOL_FOR_GAS) {
				return
			}
		}
		// Continue anyway - tx might still work if faucet tx is pending
		console.warn('[ensureGasBalance] Timeout waiting for gas, continuing anyway...')
	}
}
